<template>
	<div class="entry-table">
		<div class="flex-table" role="table-head">
			<div
				:class="parent && parent.parentPoId ? 'flex-table-row' : 'flex-table-row parent-item'"
				role="row-group"
			>
				<div class="flex-table-cell text-center" role="column-header">&nbsp;</div>
				<div class="flex-table-cell" role="column-header">STYLE CODE</div>
				<div class="flex-table-cell" role="column-header">DESCRIPTION</div>
				<div class="flex-table-cell text-center" role="column-header">SIZE</div>
				<div class="flex-table-cell text-center" role="column-header">ORDERED</div>
				<div class="flex-table-cell text-center" role="column-header">SHIPPED</div>
				<div class="flex-table-cell text-center" role="column-header">SHIPPING DATE</div>
				<div class="flex-table-cell text-center" role="column-header">TRACKING NUMBER</div>
			</div>
		</div>
		<div class="flex-table" role="table-body">
			<div
				v-for="(data, i) in tableData"
				:key="i"
				:class="parent && parent.parentPoId ? 'flex-table-row' : 'flex-table-row parent-item'"
				role="row-group"
			>
				<div class="d-flex flex-column flex-table-cell justify-center align-center" role="cell">
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<div class="dropship-icons">
								<v-icon large v-bind="attrs" v-on="on"> mdi-truck-fast </v-icon>
							</div>
						</template>
						<span>Dropship</span>
					</v-tooltip>
				</div>
				<div class="d-flex flex-table-cell" role="cell">
					<v-icon v-if="parent && parent.parentPoId" class="mr-4"
						>mdi-subdirectory-arrow-right</v-icon
					>
					<SystemCategoryIcon :system-category-id="data.systemCategoryId" />
					{{ data.itemOrdered.styleCode }}
				</div>
				<div class="d-flex flex-table-cell" role="cell">
					{{ data.itemOrdered.description }}
				</div>
				<div class="flex-table-cell text-center" role="cell">
					<div class="size-quantity-wrapper">
						<div
							v-for="(sizeItem, idx) in combinedSizes(data)"
							:key="idx"
						>
							<div
								v-for="(dropShipRecord, j) in sizeItem.dropShipRecords"
								:key="j"
								class="size-quantity-row"
							>
								<input
									:style="j === 0 ? 'visibility:visible;' : 'visibility:hidden;'"
									class="custom-text-input b-none"
									outlined
									dense
									hide-details="auto"
									:value="sizeItem.length ? `${sizeItem.size}/${sizeItem.length}` : sizeItem.size"
									disabled
									readonly
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell text-center" role="cell">
					<div class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0">
							<div
								v-for="(dropShipRecord, j) in data.dropShipRecords"
								:key="j"
								class="size-quantity-row"
							>
								<input
									:style="j === 0 ? 'visibility:visible;' : 'visibility:hidden;'"
									class="custom-text-input b-none"
									outlined
									dense
									hide-details="auto"
									v-model="data.quantity"
									disabled
									readonly
								/>
							</div>
						</div>
						<div v-else>
							<div v-for="(sizeItem, idx) in combinedSizes(data)" :key="idx">
								<div
									v-for="(dropShipRecord, j) in sizeItem.dropShipRecords"
									:key="j"
									class="size-quantity-row"
								>
									<input
										:style="j === 0 ? 'visibility:visible;' : 'visibility:hidden;'"
										class="custom-text-input b-none"
										outlined
										dense
										hide-details="auto"
										v-model="sizeItem.quantity"
										disabled
										readonly
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0">
							<div
								v-for="(dropShipRecord, j) in data.dropShipRecords"
								:key="j"
								class="size-quantity-row"
							>
								<input
									class="custom-text-input"
									outlined
									dense
									hide-details="auto"
									v-model="dropShipRecord.shipped"
									type="number"
									:min="0"
									:max="getMaxShippedQuantity(data, dropShipRecord, j)"
									:disabled="!canReceive"
									@keyup="(e) => handleShippedQuantity(dropShipRecord, data, e.target.value)"
									@change="$forceUpdate()"
								/>
							</div>
						</div>
						<div v-else>
							<div v-for="(sizeItem, idx) in combinedSizes(data)" :key="idx">
								<div
									v-for="(dropShipRecord, j) in sizeItem.dropShipRecords"
									:key="j"
									class="size-quantity-row"
								>
									<input
										class="custom-text-input"
										outlined
										dense
										hide-details="auto"
										v-model="dropShipRecord.shipped"
										type="number"
										:min="0"
										:max="getMaxShippedQuantity(sizeItem, dropShipRecord, j)"
										:disabled="!canReceive"
										@keyup="(e) => handleShippedQuantity(dropShipRecord, sizeItem, e.target.value)"
										@change="$forceUpdate()"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0">
							<div
								v-for="(dropShipRecord, j) in data.dropShipRecords"
								:key="j"
								class="size-quantity-row"
							>
								<v-menu
									v-model="dropShipRecord.showDatePicker"
									:close-on-content-click="false"
									:nudge-right="40"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											:value="
												formatDate(dropShipRecord.shipDate || dropShipRecord.formattedShipDate)
											"
											prepend-inner-icon="mdi-calendar"
											outlined
											dense
											readonly
											hide-details="auto"
											v-bind="attrs"
											v-on="on"
											@blur="
												dropShipRecord.shipDate = parseDate(dropShipRecord.formattedShipDate);
												$forceUpdate();
											"
											clearable
										></v-text-field>
									</template>
									<v-date-picker
										v-model="dropShipRecord.shipDate"
										@input="
											dropShipRecord.showDatePicker = false;
											$forceUpdate();
										"
										:max="today"
									></v-date-picker>
								</v-menu>
							</div>
						</div>
						<div v-else>
							<div v-for="(sizeItem, idx) in combinedSizes(data)" :key="idx">
								<div
									v-for="(dropShipRecord, j) in sizeItem.dropShipRecords"
									:key="j"
									class="size-quantity-row"
								>
									<v-menu
										v-model="dropShipRecord.showDatePicker"
										:close-on-content-click="false"
										:nudge-right="40"
										transition="scale-transition"
										offset-y
										min-width="auto"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="
													formatDate(dropShipRecord.shipDate || dropShipRecord.formattedShipDate)
												"
												prepend-inner-icon="mdi-calendar"
												outlined
												dense
												readonly
												hide-details="auto"
												v-bind="attrs"
												v-on="on"
												@blur="
													dropShipRecord.shipDate = parseDate(dropShipRecord.formattedShipDate);
													$forceUpdate();
												"
												clearable
											></v-text-field>
										</template>
										<v-date-picker
											v-model="dropShipRecord.shipDate"
											@input="
												dropShipRecord.showDatePicker = false;
												$forceUpdate();
											"
											:max="today"
										></v-date-picker>
									</v-menu>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0">
							<div
								v-for="(dropShipRecord, j) in data.dropShipRecords"
								:key="j"
								class="size-quantity-row"
								@mouseover="hoveredSizeIndex = dropShipRecord"
								@mouseleave="hoveredSizeIndex = null"
							>
								<div class="tracking-input-wrapper">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="dropShipRecord.trackingNumber"
										:append-outer-icon="
											shouldRenderAddShipmentRecord(data) &&
											Number(dropShipRecord.shipped) !== 0 &&
											j + 1 === data.dropShipRecords?.length
												? 'mdi-plus-circle-outline'
												: ''
										"
										:disabled="!canReceive"
										@click:append-outer="
											() => {
												data.dropShipRecords?.push({
													id: 0,
													lineItemId: data.lineItemId || data.salesOrderLineItemId,
													itemId: data.itemOrdered?.inventoryItemId,
													purchaseOrderId: data.purchaseOrderId,
													sizeId: null,
													name: null,
													sizeName: null,
													lengthId: null,
													length: null,
													ordered: data.ordered,
													shipped: 0,
													showDatePicker: false,
													shipDate: null,
													formattedShipDate: null,
													trackingNumber: null,
												});
												$forceUpdate();
											}
										"
									></v-text-field>
									<v-btn
										v-if="data.dropShipRecords?.length !== 1 && hoveredSizeIndex === dropShipRecord"
										icon
										plain
										@click="
											() => {
												data.dropShipRecords?.splice(j, 1);
												$forceUpdate();
											}
										"
									>
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</div>
							</div>
						</div>
						<div v-else>
							<div v-for="(sizeItem, idx) in combinedSizes(data)" :key="idx">
								<div
									v-for="(dropShipRecord, j) in sizeItem.dropShipRecords"
									:key="j"
									class="size-quantity-row"
									@mouseover="hoveredSizeIndex = dropShipRecord"
									@mouseleave="hoveredSizeIndex = null"
								>
									<div class="tracking-input-wrapper">
										<v-text-field
											outlined
											dense
											hide-details="auto"
											v-model="dropShipRecord.trackingNumber"
											:append-outer-icon="
												shouldRenderAddShipmentRecord(sizeItem) &&
												Number(dropShipRecord.shipped) !== 0 &&
												j + 1 === sizeItem.dropShipRecords?.length
													? 'mdi-plus-circle-outline'
													: ''
											"
											:disabled="!canReceive"
											@click:append-outer="
												() => {
													sizeItem.dropShipRecords?.push({
														id: 0,
														lineItemId: data.lineItemId || data.salesOrderLineItemId,
														itemId: data.itemOrdered?.inventoryItemId,
														purchaseOrderId: data.purchaseOrderId,
														sizeId: sizeItem.sizeId,
														name: sizeItem.size,
														sizeName: sizeItem.size,
														lengthId: sizeItem.lengthId,
														length: sizeItem.length,
														ordered: sizeItem.ordered,
														shipped: 0,
														showDatePicker: false,
														shipDate: null,
														formattedShipDate: null,
														trackingNumber: null,
													});
													$forceUpdate();
												}
											"
										></v-text-field>
										<v-btn
											v-if="
												sizeItem.dropShipRecords?.length !== 1 &&
												hoveredSizeIndex === dropShipRecord
											"
											icon
											plain
											@click="
												() => {
													sizeItem.dropShipRecords?.splice(j, 1);
													$forceUpdate();
												}
											"
										>
											<v-icon>mdi-close</v-icon>
										</v-btn>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import DateFormatting from "@/mixins/DateFormatting";
import gql from "graphql-tag";
import { markDropShipBaseline } from "@/helpers/dropShipChanges";

const GET_DROP_SHIP_RECORDS = gql`
	query GetDropShipRecordsByPurchaseOrderIdAndLineItemId(
		$purchaseOrderId: Int!
		$lineItemId: Long
	) {
		dropShipRecords(
			where: { purchaseOrderId: { eq: $purchaseOrderId }, lineItemId: { eq: $lineItemId } }
		) {
			items {
				lineItemId
				itemId
				purchaseOrderId
				sizeId
				sizeName
				lengthId
				length
				ordered
				shipped
				shipDate
				trackingNumber
			}
		}
	}
`;

export default {
	name: "DropshipEntryTable",
	mixins: [DateFormatting],
	components: {
		SystemCategoryIcon,
	},
	props: {
		value: {
			type: Array,
			default() {
				return [];
			},
		},
		parent: {
			type: Object,
			default() {
				return null;
			},
		},
		selectAll: {
			type: Boolean,
			default: false,
		},
		canReceive: {
			type: Boolean,
			default: false,
		},
		updateKey: {
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			tableData: [],
			today: new Date().toISOString().slice(0, 10),
			hoveredSizeIndex: null,
			dropShipRecords: [],
		};
	},
	methods: {
		// Regular and special sizes live in separate arrays but display as one
		// list ordered by the shared displayOrder rank stamped in
		// ReceivingView.refactorResponse (size chart sequence across both kinds).
		combinedSizes(row) {
			return [...row.sizes, ...(row.specialSizes || [])].sort(
				(a, b) =>
					(a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
					(b.displayOrder ?? Number.MAX_SAFE_INTEGER)
			);
		},
		getMaxShippedQuantity(size, dropShipRecord, index) {
			if (index === 0 && size.dropShipRecords?.length === 1) {
				return size.ordered;
			}

			const totalShipped =
				size.dropShipRecords?.reduce((sum, record) => {
					if (record !== dropShipRecord) {
						return sum + Number(record.shipped);
					}
					return sum;
				}, 0) || 0;

			return size.ordered - totalShipped;
		},
		shouldRenderAddShipmentRecord(size) {
			if (size.ordered === 1) {
				return false;
			}

			const totalShipped =
				size.dropShipRecords?.reduce((sum, record) => sum + Number(record.shipped), 0) || 0;
			return totalShipped < size.ordered;
		},
		async handleShippedQuantity(dropShipRecord, size, input) {
			let inputValue = input === "" ? 0 : parseInt(input);

			// Calculate the total shipped quantity for the size excluding the current dropShipRecord
			const totalShipped =
				size.dropShipRecords?.reduce((sum, record) => {
					if (record !== dropShipRecord) {
						// exclude the calculation of the current record
						return Number(sum) + Number(record.shipped);
					}
					return sum;
				}, 0) || 0;

			let fixedInputValue;

			if (Number(totalShipped) + Number(inputValue) > Number(size.ordered)) {
				// If the total shipped quantity exceeds the ordered quantity, set the input value to the maximum allowed
				fixedInputValue = Number(size.ordered) - Number(totalShipped);
			} else {
				// Allow single input of 0 but strip leading 0 if input is a 2-digit number or more
				fixedInputValue =
					input.length > 1
						? input.replace(/^0+/, "").replace(/[^\d.]/gi, "")
						: input.replace(/[^\d.]/gi, "");
			}

			// Update the shipped quantity of the dropShipRecord
			dropShipRecord.shipped = fixedInputValue === "" ? 0 : parseInt(fixedInputValue);
			this.$forceUpdate();
		},
		async shipAllSizes(item) {
			const defaultDate = this.parseDate(this.formatDate(this.today));

			if (!item.sizes || item.sizes.length === 0) {
				// If item has no sizes
				const totalShipped =
					item.dropShipRecords?.reduce((sum, record) => Number(sum) + Number(record.shipped), 0) ||
					0;
				const remainingQuantity = Number(item.quantity) - Number(totalShipped);

				if (remainingQuantity > 0) {
					const lastRecord = item.dropShipRecords?.[item.dropShipRecords.length - 1];

					if (lastRecord && Number(lastRecord.shipped) === 0) {
						lastRecord.shipped = remainingQuantity;

						if (!lastRecord.shipDate) {
							lastRecord.shipDate = defaultDate;
							lastRecord.formattedShipDate = defaultDate;
						}
					} else {
						item.dropShipRecords?.push({
							id: 0,
							lineItemId: item.lineItemId || item.salesOrderLineItemId,
							itemId: item.itemOrdered?.inventoryItemId,
							purchaseOrderId: item.purchaseOrderId,
							sizeId: null,
							name: null,
							sizeName: null,
							lengthId: null,
							length: null,
							ordered: item.quantity,
							shipped: remainingQuantity,
							showDatePicker: false,
							shipDate: defaultDate,
							formattedShipDate: defaultDate,
							trackingNumber: null,
						});
					}
				}
			} else {
				// Process regular sizes
				item.sizes.forEach((size) => {
					const totalShipped =
						size.dropShipRecords?.reduce(
							(sum, record) => Number(sum) + Number(record.shipped),
							0
						) || 0;
					const remainingQuantity = Number(size.ordered) - Number(totalShipped);

					size.dropShipRecords?.forEach((dsr) => {
						if (!dsr.shipDate || dsr.shipDate == "") {
							dsr.shipDate = defaultDate;
							dsr.formattedShipDate = defaultDate;
						}

						dsr.sizeName = dsr.name || dsr.size || dsr.sizeName;
					});

					if (remainingQuantity > 0) {
						const lastRecord = size.dropShipRecords?.[size.dropShipRecords.length - 1];

						if (lastRecord && Number(lastRecord.shipped) === 0) {
							lastRecord.shipped = remainingQuantity;

							if (!lastRecord.shipDate) {
								lastRecord.shipDate = defaultDate;
								lastRecord.formattedShipDate = defaultDate;
							}
						} else {
							size.dropShipRecords?.push({
								id: 0,
								lineItemId: item.lineItemId || item.salesOrderLineItemId,
								itemId: item.itemOrdered?.inventoryItemId,
								purchaseOrderId: item.purchaseOrderId,
								sizeId: size.sizeId,
								name: size.size,
								sizeName: size.size,
								lengthId: size.lengthId,
								length: size.length,
								ordered: size.ordered,
								shipped: remainingQuantity,
								showDatePicker: false,
								shipDate: defaultDate,
								formattedShipDate: defaultDate,
								trackingNumber: null,
							});
						}
					}
				});

				// Process special sizes
				if (item.specialSizes && item.specialSizes.length > 0) {
					item.specialSizes.forEach((size) => {
						const totalShipped =
							size.dropShipRecords?.reduce(
								(sum, record) => Number(sum) + Number(record.shipped),
								0
							) || 0;
						const remainingQuantity = Number(size.ordered) - Number(totalShipped);

						size.dropShipRecords?.forEach((dsr) => {
							if (!dsr.shipDate || dsr.shipDate == "") {
								dsr.shipDate = defaultDate;
								dsr.formattedShipDate = defaultDate;
							}

							dsr.sizeName = dsr.name || dsr.size || dsr.sizeName;
						});

						if (remainingQuantity > 0) {
							const lastRecord = size.dropShipRecords?.[size.dropShipRecords.length - 1];

							if (lastRecord && Number(lastRecord.shipped) === 0) {
								lastRecord.shipped = remainingQuantity;

								if (!lastRecord.shipDate) {
									lastRecord.shipDate = defaultDate;
									lastRecord.formattedShipDate = defaultDate;
								}
							} else {
								size.dropShipRecords?.push({
									id: 0,
									lineItemId: item.lineItemId || item.salesOrderLineItemId,
									itemId: item.itemOrdered?.inventoryItemId,
									purchaseOrderId: item.purchaseOrderId,
									sizeId: null,
									name: size.size,
									sizeName: size.size,
									lengthId: null,
									length: size.length,
									ordered: size.ordered,
									shipped: remainingQuantity,
									showDatePicker: false,
									shipDate: defaultDate,
									formattedShipDate: defaultDate,
									trackingNumber: null,
								});
							}
						}
					});
				}
			}
			this.$forceUpdate();
		},
		async fetchDropShipRecords(purchaseOrderId, lineItemId) {
			const res = await this.$apollo.query({
				query: GET_DROP_SHIP_RECORDS,
				variables: {
					purchaseOrderId,
					lineItemId,
				},
				fetchPolicy: "no-cache",
			});
			return res.data.dropShipRecords?.items || [];
		},
		async init() {
			this.tableData = [...this.value];
			this.tableData.forEach(async (item) => {
				const dsrs = await this.fetchDropShipRecords(
					item.purchaseOrderId,
					item.salesOrderLineItemId
				);

				if (
					(!item.sizes || item.sizes.length === 0) &&
					(!item.specialSizes || item.specialSizes.length === 0)
				) {
					item.dropShipRecords = dsrs.filter(
						(record) => record.itemId === item.itemOrdered?.inventoryItemId
					);
					// Item has no sizes or special sizes, create a default dropShipRecord for the item
					if (!item.dropShipRecords || item.dropShipRecords.length === 0) {
						item.dropShipRecords = [
							{
								id: 0,
								lineItemId: item.lineItemId || item.salesOrderLineItemId,
								itemId: item.itemOrdered?.inventoryItemId,
								purchaseOrderId: item.purchaseOrderId,
								sizeId: null,
								name: null,
								sizeName: null,
								lengthId: null,
								length: null,
								ordered: item.quantity,
								shipped: 0,
								showDatePicker: false,
								shipDate: null,
								formattedShipDate: null,
								trackingNumber: null,
							},
						];
					} else {
						// ensure meta data is applied, preserve existing data values
						item.dropShipRecords.forEach((dsr) => {
							dsr.lineItemId = item.lineItemId || item.salesOrderLineItemId;
							dsr.itemId = item.itemOrdered?.inventoryItemId;
							dsr.purchaseOrderId = item.purchaseOrderId;
							dsr.sizeId = null;
							dsr.name = null;
							dsr.sizeName = null;
							dsr.lengthId = null;
							dsr.length = null;
							dsr.ordered = item.quantity;
							dsr.showDatePicker = false;
						});
					}
					this.$forceUpdate();
				} else {
					if (item.sizes && item.sizes.length > 0) {
						// Iterate over each size object
						item.sizes.forEach(async (size) => {
							size.dropShipRecords = dsrs.filter(
								(record) => record.sizeId === size.sizeId && record.lengthId === size.lengthId
							);
							if (!size.dropShipRecords || size.dropShipRecords.length === 0) {
								// Create a default dropShipRecord if none exists
								size.dropShipRecords = [
									{
										id: 0,
										lineItemId: item.lineItemId || item.salesOrderLineItemId,
										itemId: item.itemOrdered?.inventoryItemId,
										purchaseOrderId: item.purchaseOrderId,
										sizeId: size.sizeId,
										name: size.size,
										sizeName: size.size,
										lengthId: size.lengthId,
										length: size.length,
										ordered: size.quantity,
										shipped: 0,
										showDatePicker: false,
										shipDate: null,
										formattedShipDate: null,
										trackingNumber: null,
									},
								];
							} else {
								// ensure meta data is applied, preserve existing data values
								size.dropShipRecords.forEach((dsr) => {
									dsr.lineItemId = item.lineItemId || item.salesOrderLineItemId;
									dsr.itemId = item.itemOrdered?.inventoryItemId;
									dsr.purchaseOrderId = item.purchaseOrderId;
									dsr.sizeId = size.sizeId;
									dsr.sizeName = size.size;
									dsr.lengthId = size.lengthId;
									dsr.length = size.length;
									dsr.name = size.size;
									dsr.ordered = size.quantity;
									dsr.showDatePicker = false;
								});
							}
						});
						this.$forceUpdate();
					}

					if (item.specialSizes && item.specialSizes.length > 0) {
						// Iterate over each special size object
						item.specialSizes.forEach(async (size) => {
							size.dropShipRecords = dsrs.filter(
								(record) => record.sizeName === size.size && record.length === size.length
							);
							if (!size.dropShipRecords || size.dropShipRecords.length === 0) {
								// Create a default dropShipRecord if none exists
								size.dropShipRecords = [
									{
										id: 0,
										lineItemId: item.lineItemId || item.salesOrderLineItemId,
										itemId: item.itemOrdered?.inventoryItemId,
										purchaseOrderId: item.purchaseOrderId,
										sizeId: null,
										name: size.size,
										sizeName: size.size,
										lengthId: size.lengthId,
										length: size.length,
										ordered: size.quantity,
										shipped: 0,
										showDatePicker: false,
										shipDate: null,
										formattedShipDate: null,
										trackingNumber: null,
									},
								];
							} else {
								// ensure meta data is applied, preserve existing data values
								size.dropShipRecords.forEach((dsr) => {
									dsr.lineItemId = item.lineItemId || item.salesOrderLineItemId;
									dsr.itemId = item.itemOrdered?.inventoryItemId;
									dsr.purchaseOrderId = item.purchaseOrderId;
									dsr.sizeId = size.sizeId;
									dsr.sizeName = size.size;
									dsr.lengthId = size.lengthId;
									dsr.length = size.length;
									dsr.name = size.size;
									dsr.ordered = size.quantity;
									dsr.showDatePicker = false;
								});
							}
						});
						this.$forceUpdate();
					}
				}

				const totalShipped =
					dsrs
						.filter((record) => record.itemId === item.itemOrdered?.inventoryItemId)
						?.reduce((sum, record) => {
							return sum + Number(record.shipped);
						}, 0) || 0;

				item.shipped = totalShipped;

				// Records are complete for this item: later edits are what Save sends (see helper)
				markDropShipBaseline(item);
			});

			this.$forceUpdate();
		},
	},
	watch: {
		selectAll: {
			handler: function () {
				if (Array.isArray(this.value) && this.canReceive) {
					this.value.forEach((item) => {
						this.shipAllSizes(item);
					});
				}
			},
			deep: true,
		},
		tableData: {
			handler: function () {
				this.$forceUpdate();
			},
			deep: true,
		},
		updateKey: {
			handler: function () {
				this.init();
			},
		},
	},
	async mounted() {
		await this.init();
	},
};
</script>

<style lang="scss">
.custom-text-input {
	flex: 1 1 auto;
	line-height: 24px;
	width: 100%;
	text-align: center;
	font-size: 16px;
	padding: 0px 12px;
	border-color: rgb(118, 118, 118);
	border-style: solid;
	border-width: 1px;
	border-radius: 4px;
	height: 36px;

	&:focus {
		border-width: 2px;
		border-color: #c2262f;
		outline: none;
	}
}

.b-none {
	border: none !important;
}
</style>

<style lang="scss" scoped>
.entry {
	&-table {
		position: relative;
		overflow-y: auto;
		background-color: $color-white;
		border: 1px solid #bbbbbb;
		border-radius: 3px;

		.form-input {
			margin: 0;
		}

		.flex-table {
			border: none;

			&[role="table-head"] {
				position: sticky;
				top: 0;
				z-index: 1;

				.flex-table-row {
					background-color: rgba(224, 224, 224, 0.6);
				}
			}

			&[role="table-body"] {
				.flex-table-row {
					border-top: none;
					border-bottom: solid 1px $color-gray;
				}
			}
		}

		.flex-table-cell[role="column-header"] {
			font-size: 1.4rem;
		}

		.flex-table-cell {
			font-size: 1.6rem;
			display: block;

			&:nth-child(1) {
				width: 5%;
			}

			&:nth-child(2) {
				width: 20%;
			}

			&:nth-child(3) {
				width: 15%;
			}

			&:nth-child(4) {
				width: 15%;
			}

			&:nth-child(5) {
				width: 9%;
			}

			&:nth-child(6) {
				width: 12%;
			}

			&:nth-child(7) {
				width: 12%;
			}

			&:nth-child(8) {
				width: 12%;
			}
		}
	}
}
</style>

<style lang="scss">
.parent-item {
	padding-left: 25px;
}
</style>
